/**
 * The canonical segmented control — log tabs, log levels, metric ranges.
 * An 8px rail holding pills; the active pill lifts on the one xs shadow.
 * Replaces the loose chips those filters used to be.
 */
export interface Segment<T extends string> {
  value: T,
  label: React.ReactNode,
  /**
   * Shown, and refused. For a choice that exists but cannot be made right now
   * — the caller is expected to say why somewhere the eye will reach, because
   * a segment that will not depress and gives no reason reads as broken.
   */
  isDisabled?: boolean,
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[],
  value: T,
  onChange: (value: T) => void,
  /** 24px on desktop, 32px where the control is a touch target. */
  size?: 'sm' | 'touch',
  ariaLabel?: string,
  className?: string,
}

const SegmentedControl = <T extends string>({
  segments,
  value,
  onChange,
  size = 'sm',
  ariaLabel,
  className = '',
}: SegmentedControlProps<T>) => (
  <div role="tablist" aria-label={ariaLabel} className={`b4-segmented ${className}`}>
    {segments.map(segment => {
      const isActive = segment.value === value;
      return (
        <button
          key={segment.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-disabled={segment.isDisabled || undefined}
          disabled={segment.isDisabled}
          onClick={() => onChange(segment.value)}
          className={[
            'b4-focus font-inter text-ds-125 font-medium rounded-[4px]',
            'transition-colors duration-[120ms]',
            size === 'sm' ? 'h-6 px-[11px]' : 'h-8 px-3',
            segment.isDisabled
              ? 'bg-transparent text-navy-400 cursor-not-allowed'
              : isActive
              ? 'bg-navy-800 text-white shadow-[0_1px_2px_rgba(0,0,0,.4)] cursor-pointer'
              : 'bg-transparent text-navy-200 hover:text-white cursor-pointer',
          ].join(' ')}
        >
          {segment.label}
        </button>
      );
    })}
  </div>
)

export default SegmentedControl
